import { getCookieConsent } from "../../../consent/cookieUtils";
import { getFeatureData } from "../../utils/features";
import { matomoActions } from "../../actions/front-end/triggerMatomoEvents";
import { loadFeature } from "../../utils/features";

type TrackingEventData = {
    category?: string;
    name?: string;
    action?: matomoActions;
    pageTitle?: string;
    linkType?: "link" | "download";
    href?: string;
};
/**
 * @private
 * @type {TrackingEventData[]}
 * @description A queue to store tracking events that occur before Matomo tracking is fully initialized.
 */
const unpublishedEvents: TrackingEventData[] = [];
/**
 * @private
 * @type {boolean}
 * @description Flag indicating whether Matomo tracking has been set up.
 */
let matomo_tracking_setup_complete = false;
/**
 * @private
 * @type {boolean}
 * @description Flag indicating whether GTM tracking has been set up (script loaded).
 */
let gtm_tracking_setup_complete = false;
/**
 * @private
 * @type {boolean}
 * @description Flag indicating whether Matomo consent is expressed (denied or accepted).
 */
let matomo_consent_expressed = false;
/**
 * @private
 * @type {string}
 * @description Unique identifier for the page visit.
 */
let pvId: string = "";

/**
 * @async
 * @function trackPageVisit
 * @description Tracks a page visit event with the specified page title.
 * If Matomo tracking is not yet initialized, the event is added to the `unpublishedEvents` queue.
 * @param {string} pageTitle - The title of the page being visited.
 * @returns {Promise<void>} - Resolves when the event has been processed or queued.
 * @example
 * trackPageVisit("Home Page");
 */
export async function trackPageVisit(pageTitle: string) {
    pvId = generateUniquePVId();
    if (!matomo_tracking_setup_complete) {
        unpublishedEvents.push({ pageTitle });
        return;
    }
    return triggerMatomoDataLayerEvent({ action_name: pageTitle });
}

/**
 * @async
 * @function trackEvent
 * @description Tracks a custom event with the provided event data.
 * If Matomo tracking is not yet initialized, the event is added to the `unpublishedEvents` queue.
 * @param {TrackingEventData} [matomoEvent={}] - The data associated with the event.
 * @returns {Promise<void>} - Resolves when the event has been processed or queued.
 * @example
 * trackEvent({ category: "Button", name: "Click", action: matomoActions.BUTTON_CLICK });
 */
export async function trackEvent(matomoEvent: TrackingEventData = {}) {
    if (!matomo_tracking_setup_complete) {
        unpublishedEvents.push(matomoEvent);
        return;
    }
    return triggerMatomoDataLayerEvent({
        e_a: matomoEvent.action,
        e_c: matomoEvent.category,
        e_n: matomoEvent.name
    });
}
/**
 *
 * @param matomoEvent
 * @returns
 */
export async function trackOutgoingLink(matomoEvent: TrackingEventData = {}) {
    if (!matomo_tracking_setup_complete) {
        unpublishedEvents.push(matomoEvent);
        return;
    }
    const eventAttributes: { download?: string; link?: string } = {};
    if (matomoEvent.linkType === "download") {
        eventAttributes.download = matomoEvent.href;
    } else if (matomoEvent.linkType === "link") {
        eventAttributes.link = matomoEvent.href;
    } else {
        return;
    }
    return triggerMatomoDataLayerEvent(eventAttributes);
}
/**
 * @async
 * @private
 * @function getAdditionalMatomoTrackingData
 * @description Gathers additional data for Matomo tracking, such as URL, referrer, language, screen resolution, user agent, and timestamps.
 * @returns {Promise<object>} - An object containing the additional Matomo tracking data.
 */
const getAdditionalMatomoTrackingData = async () => {
    const { uadata, ua } = await getUserAgentData();
    return {
        url: window.location.href,
        urlref: getReferrer(),
        lang: navigator.language,
        res: `${window.screen.width}x${window.screen.height}`,
        _id: "",
        ua: ua,
        uadata: uadata,
        h: new Date().getHours(),
        m: new Date().getMinutes(),
        s: new Date().getSeconds(),
        pv_id: pvId,
        cookie: 1,
        send_image: "0" // Prevents Matomo from returning a tracking pixel
    };
};

/**
 * @async
 * @private
 * @function getUserAgentData
 * @description Extracts detailed user agent information, including brands, model, platform, and versions.
 * @returns {Promise<{ ua: string; uadata?: string }>} - An object containing the user agent string and optional high-entropy user agent data.
 */
const getUserAgentData = async () => {
    const data: { ua: string; uadata?: string } = { ua: navigator.userAgent };
    if ((window as any)?.navigator.userAgentData) {
        data.uadata = JSON.stringify(
            await (window as any)?.navigator.userAgentData
                .getHighEntropyValues([
                    "brands",
                    "model",
                    "platform",
                    "platformVersion",
                    "uaFullVersion",
                    "fullVersionList",
                    "formFactors"
                ])
                .then(function(ua) {
                    if (ua.fullVersionList) {
                        // if fullVersionList is available, brands and uaFullVersion isn't needed
                        delete ua.brands;
                        delete ua.uaFullVersion;
                    }
                    return ua;
                })
        );
    }
    return data;
};

/**
 * @typedef {Object} MatomoData
 * @property {string} [action_name] - The name of the action being tracked (used for page views).
 * @property {string} [e_a] - The action of the event.
 * @property {string} [e_c] - The category of the event.
 * @property {string} [e_n] - The name of the event.
 * @property {number} [rec] - Indicates whether to record the event (usually 1).
 * @property {string} [key] - Any other custom parameter to be passed to Matomo
 */
interface MatomoData {
    action_name?: string;
    e_a?: string;
    e_c?: string;
    e_n?: string;
    rec?: number;
    [key: string]: any;
}

/**
 * @async
 * @private
 * @function triggerMatomoDataLayerEvent
 * @description Pushes a Matomo tracking event to the dataLayer.
 * This function prepares and formats the data before sending it.
 * It also handles cases where Matomo consent is not given.
 * @param {MatomoData} [data={}] - The event data to be tracked.
 * @returns {Promise<void>} - Resolves when the event has been pushed to the dataLayer.
 */
const triggerMatomoDataLayerEvent = async (data: MatomoData = {}) => {
    const idsite = getFeatureData("matomo")?.settings?.["site-id"];
    const requiredMatomoTrackingData = { rec: 1, idsite };
    const additionalMatomoTrackingData = await getAdditionalMatomoTrackingData();
    //Matomo consent false
    let matomo_data: MatomoData = {};
    if (getCookieConsent("np6") === true) {
        const userHash = getFeatureData("matomo")?.settings?.user?.["user-hash"];
        if (userHash) {
            requiredMatomoTrackingData["id_pp"] = userHash;
        }
    }
    //Do not add data identifying a person in no consent given
    if (getCookieConsent("matomo") === true) {
        matomo_data = Object.assign(requiredMatomoTrackingData, additionalMatomoTrackingData, data);
    } else if (data.action_name) {
        //page view
        matomo_data = Object.assign(requiredMatomoTrackingData, { action_name: data.action_name });
    } else if (data.e_a) {
        //page event
        matomo_data = Object.assign(requiredMatomoTrackingData, {
            e_a: data.e_a,
            e_c: data.e_c
        });
    }
    //clear:true is a dataLayer configuration to prevent recursive merginfg of plain object variable values
    //https://github.com/google/data-layer-helper?tab=readme-ov-file#recursively-merging-values
    // console.log(matomo_data);
    (window as any)?.dataLayer.push({
        event: "tq-matomo-event",
        _clear: true,
        matomo_data
    });
};

/**
 * @typedef {Object} MatomoConfig
 * @property {string} matomo_tracking_url - The URL of the Matomo tracking server.
 * @property {string} site_id - The ID of the website in Matomo.
 * @property {string} [dimension1] - Custom dimension 1 (e.g., user connection status).
 * @property {string} [dimension2] - Custom dimension 2 (e.g., contract type).
 * @property {string} [dimension3] - Custom dimension 3 (e.g., contract role).
 */
type MatomoConfig = {
    matomo_tracking_url: string;
    site_id: string;
    dimension1?: string;
    dimension2?: string;
    dimension3?: string;
};

/**
 * @async
 * @function setMatomoConfig
 * @description Configures Matomo tracking by loading settings from the 'matomo' feature.
 * Sets the Matomo tracking URL, site ID, and custom dimensions.
 * Publishes any events that were queued before Matomo was initialized.
 * @returns {Promise<void>} - Resolves when the Matomo configuration has been set.
 * @example
 * setMatomoConfig();
 */
const setMatomoConfig = async () => {
    const { enabled: enabledMatomo, settings } = await loadFeature("matomo");
    if (!enabledMatomo) {
        return;
    }
    // await wait1000();
    const matomo_tracking_url = settings.url;
    const site_id = settings["site-id"];
    const matomo_config: MatomoConfig = { matomo_tracking_url, site_id };
    const user = settings.user || {};
    //Do not add data identifying a person in no consent given
    if (getCookieConsent("matomo") === true) {
        if (user["connected"]) {
            matomo_config.dimension1 = user["connected"];
        }
        if (user["contract-type"]) {
            matomo_config.dimension2 = user["contract-type"];
        }
        if (user["contract-role"]) {
            matomo_config.dimension3 = user["contract-role"];
        }
    }
    matomo_tracking_setup_complete = true;
    publishUnpublishedEvents();
    (window as any)?.dataLayer.push({
        _clear: true,
        event: "tq-set-matomo-config",
        matomo_config
    });
};

/**
 * @private
 * @function publishUnpublishedEvents
 * @description Processes and publishes any events that were added to the `unpublishedEvents` queue before Matomo tracking was initialized.
 */
const publishUnpublishedEvents = () => {
    unpublishedEvents.forEach(eventData => {
        if (eventData.pageTitle) {
            trackPageVisit(eventData.pageTitle);
        } else {
            trackEvent(eventData);
        }
    });
    while (unpublishedEvents.length) {
        unpublishedEvents.pop();
    }
};

/**
 * @private
 * @function generateUniquePVId
 * @description Generates a unique 6-character alphanumeric identifier to be used as pvId
 * @returns {string} - The generated unique ID.
 */
function generateUniquePVId() {
    let id = "";
    const chars = "abcdefghijklmnopqrstuvwxyz0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const charLen = chars.length;
    let i;
    for (i = 0; i < 6; i++) {
        id += chars.charAt(Math.floor(Math.random() * charLen));
    }
    return id;
}

/**
 * @private
 * @function getReferrer
 * @description Gets the referrer URL, handling potential cross-origin access issues.
 * @returns {string} - The referrer URL.
 */
function getReferrer() {
    let referrer = "";
    try {
        referrer = window.top?.document.referrer || "";
    } catch (e) {
        if (window.parent) {
            try {
                referrer = window.parent.document.referrer;
            } catch (e2) {
                referrer = "";
            }
        }
    }
    if (referrer === "") {
        referrer = document.referrer;
    }
    return referrer;
}
/**
 * Marks that the user has expressed consent for Matomo tracking.
 * If Google Tag Manager (GTM) tracking setup is complete but Matomo tracking setup is not,
 * this function will trigger the Matomo configuration setup.
 */
export function MatomoConsentExpressed() {
    matomo_consent_expressed = true;
    if (gtm_tracking_setup_complete && !matomo_tracking_setup_complete) {
        setMatomoConfig();
    }
}
/**
 * Marks that the GTM tracking setup process has completed.
 * If Matomo consent has been expressed but Matomo tracking setup is not yet complete,
 * this function will trigger the Matomo configuration setup.
 */
export function GTMTrackingSetupComplete() {
    gtm_tracking_setup_complete = true;
    if (matomo_consent_expressed && !matomo_tracking_setup_complete) {
        setMatomoConfig();
    }
}
